import { Component, Emit, Model, Prop, Ref, Vue, Watch } from 'nuxt-property-decorator';
import { Editor, EditorContent, Extensions } from '@tiptap/vue-2';
import { TiptapExtension } from '../../types';
import TiptapMenu from './TiptapMenu.vue';
import TiptapLinkBubble from './TiptapLinkBubble.vue';

/* Extensions */
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import Selection from '@fourwaves/tiptap-extension-selection-decoration';
import { Youtube, Vimeo } from '@fourwaves/tiptap-extension-video';
import LegacyVideo from './legacy-video-plugin';
import CleanParagraphWhitespace from './clean-paragraph-whitespace';

@Component({
  components: {
    EditorContent,
    TiptapMenu,
    TiptapLinkBubble,
  },
})
export default class TiptapEditorMixin extends Vue {
  @Prop({ type: String, required: true }) readonly name!: string;
  @Prop({ type: Array, default: () => [] }) readonly extensions!: TiptapExtension[];
  @Prop({ type: String, default: '' }) readonly placeholder!: string;
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
  @Prop({ type: Boolean, default: false }) readonly autofocus!: boolean;
  @Prop({ type: String, default: '' }) readonly ariaLabelledby!: string;
  @Prop({ type: Boolean, default: false }) readonly ariaRequired!: boolean;
  @Prop({ type: Boolean, default: false }) readonly ariaInvalid!: boolean;
  @Prop({ type: String, default: '' }) readonly ariaDescribedby!: string;

  @Ref() tiptapEditor?: HTMLDivElement;

  @Model('input', { type: String, default: '' }) readonly value!: string;

  readonly TiptapExtension = TiptapExtension;

  editor: Editor | null = null;

  @Emit('input') emitInput(_value: string) {}
  @Emit('blur') emitBlur(_event?: FocusEvent) {}
  @Emit('focus') emitFocus(_event?: FocusEvent) {}
  @Emit('ready') emitReady() {}
  @Emit('keydown') emitKeydown(_event: KeyboardEvent) {}

  @Watch('value')
  onValueChange(value: string) {
    if (this.editor?.getHTML() === value) return;
    this.editor?.commands.setContent(value, false);
  }

  @Watch('disabled')
  onDisabledChange(disabled: boolean) {
    if (!this.editor) return;
    this.editor.setEditable(!disabled);
  }

  @Watch('ariaInvalid')
  onAriaInvalidChange(ariaInvalid: boolean) {
    if (!this.editor) return;
    this.setEditorAttributes({ 'aria-invalid': String(ariaInvalid) });
  }

  @Watch('ariaDescribedby')
  onAriaDescribedbyChange(ariaDescribedby: string) {
    if (!this.editor) return;
    this.setEditorAttributes({ 'aria-describedby': ariaDescribedby });
  }

  get content() {
    if (!this.editor) return '';
    return this.editor.isEmpty ? '' : this.editor.getHTML();
  }

  mounted() {
    this.editor = new Editor({
      content: this.value,
      autofocus: this.autofocus,
      editable: !this.disabled,
      editorProps: {
        attributes: {
          id: this.name,
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-required': String(this.ariaRequired),
          'aria-invalid': String(this.ariaInvalid),
          'aria-describedby': this.ariaDescribedby,
          ...(this.ariaLabelledby && { 'aria-labelledby': this.ariaLabelledby }),
        },
      },
      onUpdate: () => this.emitInput(this.content),
      onFocus: ({ event }) => this.onFocusHandler(event),
      onBlur: ({ event }) => this.onBlurHandler(event),
      onCreate: ({ editor }) => {
        editor.view.dom.classList.add('tiptap-editor__tiptap', 'rte');

        editor.view.dom.addEventListener('keydown', event => this.emitKeydown(event));

        this.emitReady();
      },
      extensions: this.buildExtensions(),
    });
  }

  public onFocusHandler(event: FocusEvent) {
    return this.emitFocus(event);
  }

  public onBlurHandler(event: FocusEvent) {
    return this.emitBlur(event);
  }

  public isOptionEnabled(option: TiptapExtension) {
    return this.extensions?.includes(option) ? undefined : false;
  }

  public isExtensionEnabled(extension: TiptapExtension) {
    return this.extensions?.includes(extension);
  }

  public buildExtensions() {
    const extensions: Extensions = [
      Selection,
      StarterKit.configure({
        bold: this.isOptionEnabled(TiptapExtension.Bold),
        italic: this.isOptionEnabled(TiptapExtension.Italic),
        heading: this.isOptionEnabled(TiptapExtension.Heading) === undefined ? { levels: [2, 3, 4] } : false,
        bulletList: this.isOptionEnabled(TiptapExtension.BulletList),
        orderedList: this.isOptionEnabled(TiptapExtension.OrderedList),
      }),
      Placeholder.configure({ placeholder: this.placeholder }),
      CleanParagraphWhitespace,
    ];

    if (this.isExtensionEnabled(TiptapExtension.Underline)) {
      extensions.push(Underline);
    }

    if (this.isExtensionEnabled(TiptapExtension.Subscript)) {
      extensions.push(Subscript);
    }

    if (this.isExtensionEnabled(TiptapExtension.Superscript)) {
      extensions.push(Superscript);
    }

    if (this.isExtensionEnabled(TiptapExtension.Link)) {
      extensions.push(
        Link.configure({
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
          openOnClick: 'whenNotEditable',
        }),
      );
    }

    if (this.isExtensionEnabled(TiptapExtension.TextAlign)) {
      extensions.push(
        TextAlign.configure({
          types: ['heading', 'paragraph'],
          alignments: ['left', 'center'],
        }),
      );
    }

    if (this.isExtensionEnabled(TiptapExtension.Video)) {
      extensions.push(Youtube);
      extensions.push(Vimeo);
      extensions.push(LegacyVideo);
    }

    if (this.isExtensionEnabled(TiptapExtension.WordCount)) {
      extensions.push(CharacterCount);
    }

    return extensions;
  }

  public setEditorAttributes(attributes: Record<string, string>) {
    if (!this.editor) return;
    this.editor.setOptions({
      editorProps: { attributes: { ...this.editor.options.editorProps.attributes, ...attributes } },
    });
  }

  beforeDestroy() {
    if (!this.tiptapEditor) return;

    /* Keeps the editor visible until page transition is over */
    const content = this.tiptapEditor.innerHTML;

    /* Remove src attribute from iframe elements to prevent them to reload */
    const strippedContent = content.replace(/<iframe[^>]*\s+src="[^"]*"[^>]*>/gi, match => {
      return match.replace(/\s+src="[^"]*"/i, '');
    });

    this.tiptapEditor.innerHTML = strippedContent;

    this.editor?.destroy();
  }
}
